import { Chip } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  NOT_READY,
  RETENTION_DAYS,
  VALID,
  SIZE_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import Checkbox from "../../../Form/Checkbox";
import { BORDER_RADIUS, META } from "../../../../utils/constants";
import {
  GRAY_COLOR_DISABLED,
  GRAY_COLOR_DISABLED_TITLE,
  GRAY_COLOR_LIGHT,
  GRAY_COLOR_TEXT_DISABLED,
  INPUT_COLOR_BORDER,
  SECONDARY_COLOR_TEXT,
} from "../../../../utils/colors";
import { numberCompactFormat } from "../../../../utils/numbers";

const CustomAudienceListItem = ({
  platform,
  customAudience,
  selectedItems,
  onChangeSelectedItems = () => {},
}) => {
  const isChecked = selectedItems.find((v) => v.id === customAudience.id);

  const isValid = customAudience.isValid ?? true;
  const isDisabled = platform !== META && !isValid;
  return (
    <CustomAudienceData
      customAudience={customAudience}
      isChecked={Boolean(isChecked)}
      isDisabled={isDisabled}
      onToggle={(e) => {
        if (e) e.stopPropagation();
        if (isDisabled) return;
        onChangeSelectedItems(
          isChecked
            ? selectedItems.filter((v) => v.id !== customAudience.id)
            : [...selectedItems, customAudience]
        );
      }}
    />
  );
};

const CustomAudienceData = ({
  customAudience,
  isChecked,
  isDisabled,
  onToggle,
}) => {
  const { t } = useTranslationApp();
  const hasRetentionDays =
    customAudience.retentionDays && customAudience.retentionDays > 0;
  const hasApproximateCount = Boolean(customAudience?.approximateCount);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gridTemplateRows:
          hasRetentionDays || hasApproximateCount ? "auto auto" : "auto",
        gap: "0 0.5rem",
        backgroundColor: isDisabled ? GRAY_COLOR_DISABLED : "fields.background",
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        borderRadius: BORDER_RADIUS,
        p: 1.2,
        width: "100%",
        minHeight: 72,
        transition: "background-color 120ms ease, border-color 120ms ease",
        cursor: isDisabled ? "not-allowed" : "pointer",
        alignItems: "center",
        ...(isDisabled
          ? {}
          : {
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }),
      }}
      role="button"
      aria-checked={Boolean(isChecked)}
      onClick={() => {
        if (isDisabled) return;
        onToggle();
      }}
    >
      <Checkbox
        disabled={isDisabled}
        edge="start"
        checked={Boolean(isChecked)}
        borderColor="#999999"
        color="#000000"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onChange={() => onToggle()}
        sx={{
          ml: 0,
          mt: 0,
          gridRow: "1",
          gridColumn: "1",
        }}
      />

      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "flex",
          alignItems: "center",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          gridRow: "1",
          gridColumn: "2",
          color: isDisabled ? GRAY_COLOR_DISABLED_TITLE : "inherit",
          ...(!hasRetentionDays && { mt: 0.3 }),
        }}
      >
        {customAudience.name}
      </Typography>

      <Chip
        size="small"
        sx={{
          backgroundColor: GRAY_COLOR_LIGHT,
          color: isDisabled ? GRAY_COLOR_TEXT_DISABLED : SECONDARY_COLOR_TEXT,
          fontWeight: 500,
          gridRow: "1",
          gridColumn: "3",
        }}
        label={t(customAudience.isValid ? VALID : NOT_READY)}
      />

      {hasRetentionDays && (
        <Typography
          variant="body2"
          sx={{
            gridRow: "2",
            gridColumn: "2",
            display: "flex",
            alignItems: "center",
            color: isDisabled ? GRAY_COLOR_TEXT_DISABLED : "text.secondary",
          }}
        >
          {`${t(RETENTION_DAYS)}: ${customAudience.retentionDays}`}
        </Typography>
      )}
      {hasApproximateCount && (
        <Typography
          variant="body2"
          sx={{
            gridRow: hasRetentionDays ? "3" : "2",
            gridColumn: "2",
            display: "flex",
            alignItems: "center",
            color: isDisabled ? GRAY_COLOR_TEXT_DISABLED : "text.secondary",
          }}
        >
          {`${t(SIZE_PLACEHOLDER)}: ${numberCompactFormat(
            customAudience?.approximateCount?.max || 0
          )}`}
        </Typography>
      )}
    </Box>
  );
};

export default CustomAudienceListItem;
